import React from 'react';
import { 
  Users, 
  Pill, 
  AlertTriangle, 
  TrendingUp, 
  DollarSign,
  Package,
  Clock,
  CheckCircle,
  Eye,
  FileText,
  Shield,
  Activity
} from 'lucide-react';

export function Dashboard() {
  const [currentTime, setCurrentTime] = React.useState(new Date());

  // Update time every second
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      title: 'Total Patients',
      value: '2,847',
      change: '+12%',
      changeType: 'positive',
      icon: Users,
      color: 'blue',
      trend: 'up'
    },
    {
      title: 'Active Prescriptions',
      value: '156',
      change: '+8%',
      changeType: 'positive',
      icon: Pill,
      color: 'green',
      trend: 'up'
    },
    {
      title: 'Low Stock Items',
      value: '23',
      change: '-5%',
      changeType: 'negative',
      icon: AlertTriangle,
      color: 'red',
      trend: 'down'
    },
    {
      title: 'Monthly Revenue',
      value: '$45,230',
      change: '+15%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'purple',
      trend: 'up'
    },
    {
      title: 'Pending Verifications',
      value: '18',
      change: '+3',
      changeType: 'neutral',
      icon: Shield,
      color: 'orange',
      trend: 'neutral'
    },
    {
      title: 'Today\'s Dispenses',
      value: '42',
      change: '+6',
      changeType: 'positive',
      icon: CheckCircle,
      color: 'emerald',
      trend: 'up'
    }
  ];

  const urgentAlerts = [
    {
      type: 'critical',
      message: 'Controlled substance count discrepancy detected',
      time: '5 minutes ago',
      action: 'Review Now'
    },
    {
      type: 'warning',
      message: '3 medications expiring within 30 days',
      time: '15 minutes ago',
      action: 'View Details'
    },
    {
      type: 'info',
      message: 'New DEA guidance update available',
      time: '1 hour ago',
      action: 'Read More'
    }
  ];

  const quickActions = [
    { 
      title: 'New Patient', 
      icon: Users, 
      color: 'blue',
      description: 'Register new patient',
      action: () => console.log('New Patient')
    },
    { 
      title: 'New Prescription', 
      icon: Pill, 
      color: 'green',
      description: 'Process new Rx',
      action: () => console.log('New Prescription')
    },
    { 
      title: 'Inventory Check', 
      icon: Package, 
      color: 'orange',
      description: 'Review stock levels',
      action: () => console.log('Inventory Check')
    },
    { 
      title: 'Insurance Verification', 
      icon: Shield, 
      color: 'purple',
      description: 'Verify coverage',
      action: () => console.log('Insurance Verification')
    },
    { 
      title: 'Patient Consultation', 
      icon: Eye, 
      color: 'indigo',
      description: 'Schedule consultation',
      action: () => console.log('Patient Consultation')
    },
    { 
      title: 'Compliance Report', 
      icon: FileText, 
      color: 'red',
      description: 'Generate reports',
      action: () => console.log('Compliance Report')
    }
  ];

  const recentActivity = [
    {
      type: 'prescription',
      message: 'New prescription for John Smith - Lisinopril 10mg',
      time: '2 minutes ago',
      status: 'pending',
      priority: 'routine'
    },
    {
      type: 'patient',
      message: 'New patient registered - Sarah Johnson',
      time: '15 minutes ago',
      status: 'completed',
      priority: 'routine'
    },
    {
      type: 'inventory',
      message: 'Low stock alert - Metformin 500mg',
      time: '1 hour ago',
      status: 'warning',
      priority: 'urgent'
    },
    {
      type: 'billing',
      message: 'Insurance claim approved - $45.20',
      time: '2 hours ago',
      status: 'completed',
      priority: 'routine'
    },
    {
      type: 'compliance',
      message: 'Monthly controlled substance report due',
      time: '3 hours ago',
      status: 'pending',
      priority: 'urgent'
    }
  ];

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-600';
      case 'warning': return 'bg-yellow-100 text-yellow-600';
      case 'pending': return 'bg-blue-100 text-blue-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-600';
      case 'stat': return 'bg-orange-100 text-orange-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#4B0082] via-[#6B38FB] to-purple-600 rounded-3xl p-10 text-white shadow-2xl shadow-[#6B38FB]/25">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-between">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-300 text-sm font-medium">System Online</span>
            </div>
            <h1 className="text-4xl font-black mb-3 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Welcome back, Dr. Johnson
            </h1>
            <p className="text-purple-100 text-xl font-medium">Here's what's happening in your pharmacy today</p>
          </div>
          
          <div className="text-right bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-xl">
            <p className="text-sm text-purple-200 font-medium mb-2">Current Time</p>
            <p className="text-3xl font-black transition-all duration-300 ease-in-out font-mono">
              {currentTime.toLocaleTimeString()}
            </p>
            <div className="flex items-center justify-center mt-3">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-300 text-xs ml-2 font-medium">Live</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const colorClasses = {
            blue: 'text-[#6B38FB] bg-gradient-to-br from-[#6B38FB]/20 to-[#6B38FB]/10',
            green: 'text-green-600 bg-gradient-to-br from-green-100 to-green-50',
            red: 'text-red-600 bg-gradient-to-br from-red-100 to-red-50',
            purple: 'text-[#4B0082] bg-gradient-to-br from-[#4B0082]/20 to-[#4B0082]/10',
            orange: 'text-orange-600 bg-gradient-to-br from-orange-100 to-orange-50',
            emerald: 'text-emerald-600 bg-gradient-to-br from-emerald-100 to-emerald-50'
          };
          
          return (
            <div key={stat.title} className="group relative bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:shadow-[#6B38FB]/20 transition-all duration-500 hover:scale-105 hover:-translate-y-2 overflow-hidden">
              {/* Hover Effect Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#6B38FB]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Animated Border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#4B0082] via-[#6B38FB] to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-4 rounded-2xl ${colorClasses[stat.color as keyof typeof colorClasses]} group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <div className={`flex items-center text-xs font-semibold ${
                    stat.trend === 'up' ? 'text-green-600' : 
                    stat.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stat.trend === 'up' && <TrendingUp className="w-4 h-4 mr-1" />}
                    {stat.trend === 'down' && <TrendingUp className="w-4 h-4 mr-1 transform rotate-180" />}
                    {stat.change}
                  </div>
                </div>
                <p className="text-4xl font-black text-gray-900 mb-3 group-hover:text-[#4B0082] transition-colors duration-500">{stat.value}</p>
                <p className="text-sm font-semibold text-gray-600 group-hover:text-[#6B38FB] transition-colors duration-500">{stat.title}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Urgent Alerts */}
      {urgentAlerts.length > 0 && (
        <div className="relative bg-white rounded-2xl border border-gray-200 p-8 shadow-xl overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-50/50 to-orange-50/50"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-100 rounded-full blur-3xl opacity-30 animate-pulse"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-lg">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-gray-900">Urgent Alerts</h3>
                  <p className="text-gray-600">Critical issues requiring immediate attention</p>
                </div>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
                  {urgentAlerts.length} active alerts
                </span>
              </div>
            </div>
            
            <div className="space-y-4">
              {urgentAlerts.map((alert, index) => (
                <div key={index} className={`group p-6 rounded-xl border-2 ${getAlertColor(alert.type)} hover:shadow-lg transition-all duration-300 hover:scale-[1.02]`}>
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <p className="font-semibold text-lg">{alert.message}</p>
                      <p className="text-sm opacity-75 flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        {alert.time}
                      </p>
                    </div>
                    <button className="px-6 py-3 bg-white rounded-xl text-sm font-semibold hover:bg-gray-50 transition-all duration-300 hover:shadow-md hover:scale-105 border-2 border-current">
                      {alert.action}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="relative bg-white rounded-2xl border border-gray-200 p-8 shadow-xl overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#6B38FB]/5 to-[#4B0082]/5"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#6B38FB]/20 rounded-full blur-3xl opacity-30 animate-pulse"></div>
          
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-[#6B38FB] to-[#4B0082] rounded-2xl shadow-lg">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-gray-900">Quick Actions</h3>
                <p className="text-gray-600">Launch essential tasks instantly</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.title}
                    onClick={action.action}
                    className="group relative flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 hover:border-[#6B38FB] hover:bg-gradient-to-br hover:from-[#6B38FB]/10 hover:to-[#4B0082]/10 transition-all duration-500 hover:scale-105 hover:-translate-y-2 overflow-hidden"
                  >
                    {/* Hover Effect Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#6B38FB]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    <div className="relative z-10">
                      <div className={`p-4 rounded-2xl bg-gradient-to-br from-${action.color}-100 to-${action.color}-50 text-${action.color}-600 mb-4 group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="text-sm font-semibold text-gray-700 text-center group-hover:text-[#4B0082] transition-colors duration-500">{action.title}</span>
                      <span className="text-xs text-gray-500 text-center mt-2 group-hover:text-[#6B38FB] transition-colors duration-500">{action.description}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 text-green-600 mr-2" />
            Recent Activity
          </h3>
          <div className="space-y-3">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div className={`p-2 rounded-full ${getStatusColor(activity.status)}`}>
                  {activity.status === 'completed' ? <CheckCircle className="w-4 h-4" /> :
                   activity.status === 'warning' ? <AlertTriangle className="w-4 h-4" /> :
                   <Clock className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <p className="text-sm text-gray-900 truncate">{activity.message}</p>
                    {activity.priority !== 'routine' && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(activity.priority)}`}>
                        {activity.priority}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-4 text-sm text-[#6B38FB] hover:text-[#6B38FB]/80 font-medium">
            View All Activity
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">42</p>
            <p className="text-sm text-green-700">Prescriptions Filled</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">18</p>
            <p className="text-sm text-blue-700">Patient Consultations</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">$2,847</p>
            <p className="text-sm text-purple-700">Revenue Generated</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <p className="text-2xl font-bold text-orange-600">95%</p>
            <p className="text-sm text-orange-700">Customer Satisfaction</p>
          </div>
        </div>
      </div>
    </div>
  );
}
